'use client'
import { useOrderSync } from '@/hooks/useOrderSync'
import type { OrderResponse } from '@a24/av-sdk/order-sdk'
import type { IFormattedLegend } from '@a24/av-sdk/seat-map-sdk'
import Divider from '@a24/ui/Divider'
import Icon from '@a24/ui/Icon'
import Link from '@a24/ui/Link'
import Popover from '@a24/ui/Popover'
import { usePathname } from 'next/navigation'
import { useRef } from 'react'
import CartItems from './cart-items'
import Timer from './timer'
import TimerPopover from './timer-popover'
import type { SanityCartPerformance } from './type'

type CartProps = {
  order: OrderResponse['data']['Order']
  admissions: OrderResponse['data']['Admissions']
  miscItemsDetails: OrderResponse['data']['MiscItemDetails']
  orderMiscItems: OrderResponse['data']['OrderItems']
  cartLegends: IFormattedLegend[]
  cartPerformanceData: SanityCartPerformance[]
  onCartTimeout?: () => void
}

const Cart = ({
  onCartTimeout,
  cartPerformanceData,
  cartLegends
}: CartProps) => {
  const { formattedOrder, isSyncing } = useOrderSync(cartLegends)

  const pathName = usePathname()
  const previousCartState = useRef(0)
  const popoverRef = useRef<HTMLDivElement>(null)

  const isCheckoutPage = pathName.includes('checkout')

  if (isSyncing && !formattedOrder) {
    return (
      <div className='flex justify-center items-center h-full'>
        <div className='text-center'>Loading...</div>
      </div>
    )
  }

  const {
    closestTimeout,
    totalNumberOfItems,
    serviceCharges,
    concessionCartItems,
    cartItems,
    totalAmmountToPay
  } = formattedOrder || {}

  const hasSelectedSeats = (formattedOrder?.totalNumberOfItems || 0) > 0

  if (hasSelectedSeats && previousCartState.current !== totalNumberOfItems) {
    previousCartState.current = totalNumberOfItems || 0
  }

  const latestPerformenceId =
    cartItems && cartItems.length > 0
      ? cartItems[cartItems.length - 1]?.performanceId
      : undefined

  return (
    <div className='lg:max-w-xs lg:min-w-xs'>
      <div className='px-6 lg:px-0 flex justify-between items-center mt-6 lg:mt-8'>
        <div className='body-xs'>Time Remaining</div>
        <Timer
          dateToExpiry={closestTimeout ? new Date(closestTimeout) : undefined}
          className='heading-sm'
          onTimeout={() => {
            onCartTimeout?.()
            popoverRef.current?.showPopover()
          }}
        />
      </div>
      {hasSelectedSeats && (
        <TimerPopover ref={popoverRef} performanceId={latestPerformenceId} />
      )}
      <div className='hidden lg:block heading-sm mt-16'>Your purchase</div>
      <div className='mt-6 lg:mt-10 grid gap-4 lg:gap-6'>
        <div className='hidden lg:block'>
          <CartItems
            cartPerformanceData={cartPerformanceData}
            serviceCharges={serviceCharges}
            cartItems={cartItems || []}
            concessionItems={concessionCartItems || []}
          />
        </div>
        <Divider className='hidden lg:block divider-faded' />
        <div className='hidden lg:flex justify-between heading-sm '>
          <div>Total</div>
          <div>${totalAmmountToPay || null}</div>
        </div>
        <Divider className='hidden lg:block divider-faded mt-2' />
        {!isCheckoutPage && (
          <div className='pl-6 pr-6'>
            <Link
              disabled={!hasSelectedSeats}
              aria-disabled={!hasSelectedSeats}
              href='/order/checkout'
              className={`w-full button-primary ${
                hasSelectedSeats ? '' : 'pointer-events-none opacity-50'
              }`}
            >
              Continue Booking{' '}
              <Icon name='arrow-right' width={8} height={8} className='ml-2' />
            </Link>
          </div>
        )}
        <Popover
          className='w-full h-full lg:hidden'
          closeButtonContent={
            <Icon width={24} height={24} id='close-menu-icon' name='close' />
          }
          closeButtonProps={{
            className: 'absolute top-0 right-0 z-20 cursor-pointer p-6',
            type: 'button'
          }}
          buttonProps={{
            type: 'button',
            className:
              'lg:hidden block button-secondary w-[calc(100vw-48px)] mx-6 md:w-[calc(100vw-36px)]'
          }}
          buttonContent={`Purchase summary - $${totalAmmountToPay}`}
        >
          <div className='p-6 relative '>
            <div className=' heading-md '>Your purchase</div>
            <div className='pt-6 lg:pt-10 flex flex-col relative'>
              <CartItems
                cartPerformanceData={cartPerformanceData}
                serviceCharges={serviceCharges}
                concessionItems={concessionCartItems || []}
                cartItems={cartItems || []}
              />
              <div className='fixed p-6 pb-8 bottom-0 left-0 right-0 bg-heritage-white-full'>
                <Divider className='divider-faded' />
                <div className='flex pt-6  justify-between heading-sm '>
                  <div>Total</div>
                  <div>${totalAmmountToPay || null}</div>
                </div>
              </div>
            </div>
          </div>
        </Popover>
      </div>
    </div>
  )
}

export default Cart
